import { createRoot } from 'react-dom/client';
import React from 'react';
import axios from 'axios';
import AddToCartDialog from '../add-to-cart-dialog/add-to-cart-dialog';
import EventBus from 'PyzShopUi/scripts/utils/eventBus';
import { EDialogEvents } from 'PyzShopUi/scripts/modules/events/dialogEvent';
import { EToastEvents } from 'PyzShopUi/scripts/modules/events/toastEvent';
import { IToastPayload } from 'PyzShopUi/scripts/interfaces/IToastPayload';
import { defMelevenArticleImageUrl } from 'PyzShopUi/scripts/modules/functions/melevenArticleImageUrl';
import Button from 'PyzShopUi/scripts/react/common/components/form/Button';
import { ResponseConstants } from 'PyzShopUi/scripts/constants';
import Utils from 'PyzShopUi/scripts/utils/utils';
import { useTranslationHook } from 'PyzShopUi/scripts/utils/translationHook';
import i18next from 'i18next';
import EtrackerTracking from 'PyzShopUi/scripts/etracker/EtrackerTracking';
import {IEtrackerProduct} from 'PyzShopUi/scripts/etracker/IEtrackerTracking';
import {SEARCH_HUB_EVENT_NAME_SEARCH_COLLECTOR, SEARCH_COLLECTOR_EVENT_TYPE_BASKET} from 'PyzShopUi/scripts/constants/search-hub';

interface ArticleTabVariantAddToCartProps {
    productSku: string;
    productName: string;
    productName2: string;
    productCanBeOrdered: boolean;
    productPrice: number;
    csrfToken: string;
    addToCartApiUrl: string;
    recommendationApiUrl: string;
}

const PRICE_DIVIDER = 100;

const articleTabVariantAddToCart: React.FunctionComponent<ArticleTabVariantAddToCartProps> = props => {
    const { productSku, productName, productName2, productCanBeOrdered, productPrice, csrfToken, addToCartApiUrl, recommendationApiUrl } = props;
    const { translate } = useTranslationHook();
    const [qtyValue, setQtyValue] = React.useState<number>(1);
    let addToCartDialogElementRoot;

    const addToCart = () => {
        const bodyFormData = new FormData();
        const url = `/${addToCartApiUrl}/${productSku}`;

        bodyFormData.append('quantity', qtyValue.toString());
        bodyFormData.append('_token', csrfToken);
        triggerSearchHubCollectorEvent();

        axios.post(url, bodyFormData)
            .then(handleAddToCartResponse)
            .catch(_ => {
                showErrorToast();
            });
    };
    const triggerSearchHubCollectorEvent = () => {
        const basketEvent = new CustomEvent(SEARCH_HUB_EVENT_NAME_SEARCH_COLLECTOR, { detail: { type: SEARCH_COLLECTOR_EVENT_TYPE_BASKET, id: productSku, price: productPrice }, });
        dispatchEvent(basketEvent);
    };
    const handleQtyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (Utils.isPositiveInteger(event.target.value, true)) {
            setQtyValue(parseInt(event.target.value));
        }
    };
    const handleQtyBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.target.value = qtyValue.toString();
    };
    const handleAddToCartResponse = response => {
        const productEtrackerData: IEtrackerProduct = {
            id: productSku,
            name: productName,
            price: Utils.formatPrice(Number(productPrice)).toString(),
            currency: document.querySelector('#data-etracker-product-currency').getAttribute('data-etracker-product-currency')
        };
        EtrackerTracking.insertToBasket(productEtrackerData, qtyValue);
        if (parseInt(response.data.code) !== ResponseConstants.HTTP_OK) {
            showErrorToast();

            return;
        }
        updateHeaderCartCounter(qtyValue);
        const addToCartDialog = document.querySelector('#add-to-cart-dialog');
        const addToCartDialogElement = addToCartDialog.querySelector('.add-to-cart-dialog-content');
        const dataCartPageUrlElement: HTMLElement = document.querySelector('[data-cart-page-url]');
        const cartPageUrl = dataCartPageUrlElement.dataset.cartPageUrl ?? (new URL('cart', window.location.origin)).toString();
        const defaultMelevenImageResolution = 'o_resize,w_1800,h_1800,m_limit,c_fff::o_extension,e_webp';
        const targetMelevenImageResolution = 'o_resize,w_100,h_100,m_limit,c_fff::o_extension,e_webp';

        const articleImage: HTMLImageElement = document.querySelector('.swiper-main img');
        const imageUrl = articleImage.src;
        const newImageUrl = defMelevenArticleImageUrl(
            imageUrl,
            defaultMelevenImageResolution,
            targetMelevenImageResolution
        );
        addToCartDialogElementRoot = createRoot(addToCartDialogElement);

        addToCartDialogElementRoot.render(
            <AddToCartDialog
                cartPageUrl={cartPageUrl}
                fullArticleName={productName}
                articleAmount={qtyValue}
                articleImageUrl={newImageUrl}
                recommendationApiUrl={recommendationApiUrl}
                productOverrides={{
                    id: productSku,
                    variant: productName2,
                    price: `${productPrice / PRICE_DIVIDER}`
                }}
            />);

        const addToCartDialogContent = document.querySelector('.add-to-cart-dialog-content');
        const dialogFootnoteWrapper = addToCartDialogContent.querySelector('.footnote');
        const dialogFootnoteText = addToCartDialogContent.querySelector('.footnote .price-note');

        if (dialogFootnoteText) {
            dialogFootnoteText.remove();
        }

        const footnoteText = document.querySelector('.footer-footnote-wrapper .price-note').cloneNode(true);
        if (dialogFootnoteWrapper) {
            dialogFootnoteWrapper.appendChild(footnoteText);
        }

        EventBus.getInstance().publish(EDialogEvents.OPEN_DIALOG, { id: addToCartDialog.id });
        EventBus.getInstance().subscribe(EDialogEvents.DIALOG_CLOSED, () => {
            if (addToCartDialogElementRoot) {
                addToCartDialogElementRoot.unmount();
            }
        });
    };
    const showErrorToast = () => {
        const toastPayload: IToastPayload = {
            message: translate('An error occured.', { ns: 'layout' }),
            iconClass: 'icon-close',
        };
        EventBus.getInstance().publish(EToastEvents.TOAST_FADE_IN, toastPayload);
    };
    const updateHeaderCartCounter = (addedCartItemsCount: number) => {
        const headerCartIcon: HTMLElement = document.querySelector('#header-cart .icon');
        const cartCounter = headerCartIcon.getAttribute('data-counter');
        const articlesInCartAmount: number = cartCounter ? parseInt(cartCounter, 10) : 0;

        if (!articlesInCartAmount) {
            headerCartIcon.classList.add('highlight');
            headerCartIcon.classList.add('counter');
        }

        headerCartIcon.setAttribute('data-counter', (articlesInCartAmount + addedCartItemsCount).toString());

    };
    const ariaLabel = i18next.t('Quantity', {ns: 'header'});
    const buttonAriaLabel = i18next.t('page.detail.add-to-cart', { ns: 'spryker-core' });

    return (
        <>
            {
                (productCanBeOrdered) ? (
                    <>
                        <input type="number"
                            name="product-order-qty"
                            data-allowed-chars="0-9"
                            data-regex="^[0-9]*[1-9][0-9]*$"
                            min="1"
                            max="3"
                            step="1"
                            required
                            className="article__order-qty"
                            defaultValue={qtyValue}
                            onChange={handleQtyChange}
                            onBlur={handleQtyBlur}
                            aria-label={ariaLabel}
                            data-track-id="addToBasketQuantity"
                        />
                        <Button
                            label=""
                            icon="cart"
                            iconAdditionalClass="icon-size-standard"
                            additionalClass="submit-add-to-cart button-leading-icon text-left"
                            onClick={addToCart}
                            dataQa="submit-button"
                            ariaAttributes={{ 'aria-label': buttonAriaLabel }}
                        />
                    </>
                ) :
                    <>
                        <Button
                            label=""
                            icon="cart"
                            iconAdditionalClass="icon-size-standard"
                            additionalClass="submit-add-to-cart button-leading-icon text-left"
                            isDisabled={true}
                            dataQa="submit-button"
                            ariaAttributes={{ 'aria-label': buttonAriaLabel }}
                        />
                    </>
            }
        </>
    );
};

export default articleTabVariantAddToCart;
